import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { sb } from '../../supabase';
import ConsoleShell from '../../components/admin2/ConsoleShell';

// Furthest stage a factory reached → a label + tone. Ordered most-advanced first.
function statusOf(r, isAr) {
  if (r.claimed)          return { t: isAr ? 'طالب المصنع ✅' : 'Claimed ✅',            tone: 'ok' };
  if (r.verify_returned)  return { t: isAr ? 'رجع بعد التوثيق' : 'Verified — back',       tone: 'ok' };
  if (r.verify_pending)   return { t: isAr ? 'بانتظار توثيق الإيميل' : 'Awaiting email',   tone: 'warn' };
  if (r.signup_started)   return { t: isAr ? 'بدأ التسجيل' : 'Started signup',            tone: 'warn' };
  if (r.viewed)           return { t: isAr ? 'شاف الصفحة' : 'Viewed page',               tone: 'info' };
  if (r.opened)           return { t: isAr ? 'فتح فقط' : 'Opened only',                  tone: 'info' };
  return { t: isAr ? 'ما فتح بعد' : 'Not opened', tone: 'idle' };
}
const TONE = {
  ok:   { bg: 'rgba(45,106,79,0.10)',  fg: '#2D6A4F', bd: 'rgba(45,106,79,0.28)' },
  warn: { bg: 'rgba(166,134,79,0.12)', fg: '#946A2B', bd: 'rgba(166,134,79,0.30)' },
  info: { bg: 'rgba(70,110,160,0.10)', fg: '#3C6098', bd: 'rgba(70,110,160,0.26)' },
  idle: { bg: 'rgba(0,0,0,0.04)',      fg: '#8A857B', bd: 'rgba(0,0,0,0.10)' },
};
const CHAN = {
  both:     (a) => (a ? 'واتساب + إيميل' : 'WhatsApp + Email'),
  whatsapp: (a) => (a ? 'واتساب' : 'WhatsApp'),
  email:    (a) => (a ? 'إيميل' : 'Email'),
  none:     () => '—',
};

// Best-effort E.164 digits for a wa.me deep link (China default when no country code).
function waDigits(phone) {
  if (!phone) return '';
  let d = String(phone).replace(/[^\d]/g, '');
  d = d.replace(/^0+/, '');
  if (d.length === 11 && d[0] === '1') d = '86' + d;
  return d;
}
function fmtKsa(ts, isAr) {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString(isAr ? 'ar-SA' : 'en-GB', {
      timeZone: 'Asia/Riyadh', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch { return '—'; }
}

export default function ConsoleFunnel({ user, profile, lang }) {
  const isAr = lang === 'ar';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [onlyOpened, setOnlyOpened] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    const { data, error } = await sb.rpc('claim_funnel_report');
    if (error) setErr(error.message || 'failed');
    setRows(Array.isArray(data) ? data : []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const reached = useMemo(() => rows.map((r) => {
    const claimed = !!r.claimed;
    const signup = r.signup_started || r.verify_pending || r.verify_returned || claimed;
    const viewed = r.viewed || signup;
    const opened = r.opened || viewed;
    return { invited: !!r.invited, opened, viewed, signup, claimed };
  }), [rows]);

  const n = useCallback((f) => reached.filter(f).length, [reached]);
  const steps = useMemo(() => {
    const invited = n((x) => x.invited);
    const raw = [
      { key: 'invited', ar: 'مُرسَل لهم',     en: 'Invited',        c: invited,             tone: 'idle' },
      { key: 'opened',  ar: 'فتحوا الرابط',   en: 'Opened link',    c: n((x) => x.opened),  tone: 'info' },
      { key: 'viewed',  ar: 'شافوا الصفحة',   en: 'Viewed page',    c: n((x) => x.viewed),  tone: 'info' },
      { key: 'signup',  ar: 'بدأوا التسجيل',  en: 'Started signup', c: n((x) => x.signup),  tone: 'warn' },
      { key: 'claimed', ar: 'طالبوا المصنع',  en: 'Claimed',        c: n((x) => x.claimed), tone: 'ok' },
    ];
    return raw.map((s, i) => ({
      ...s,
      pct: invited > 0 ? Math.round((s.c / invited) * 100) : 0,
      drop: i === 0 ? 0 : raw[i - 1].c - s.c,
    }));
  }, [n]);

  const pct = (a, b) => (b > 0 ? Math.round((a / b) * 100) : 0);
  const totalErrors = rows.filter((r) => r.had_error).length;
  const totalInapp = rows.filter((r) => r.inapp).length;
  const shown = onlyOpened ? rows.filter((r) => r.opened) : rows;

  const cards = [
    { label: isAr ? 'مُرسَل لهم' : 'Invited',        val: steps[0]?.c || 0, sub: '' },
    { label: isAr ? 'فتحوا الرابط' : 'Opened',       val: steps[1]?.c || 0, sub: `${steps[1]?.pct || 0}%`, tone: 'info' },
    { label: isAr ? 'بدأوا التسجيل' : 'Started',     val: steps[3]?.c || 0, sub: `${pct(steps[3]?.c || 0, steps[1]?.c || 0)}%`, tone: 'warn' },
    { label: isAr ? 'طالبوا المصنع' : 'Claimed',     val: steps[4]?.c || 0, sub: `${pct(steps[4]?.c || 0, steps[1]?.c || 0)}%`, tone: 'ok' },
  ];

  return (
    <ConsoleShell user={user} profile={profile} lang={lang} active="funnel">
      <div style={{ padding: '20px clamp(14px,4vw,30px)', maxWidth: 1180, margin: '0 auto', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: 'var(--ac-ink)' }}>{isAr ? 'قمع المطالبات' : 'Claim Funnel'}</h1>
            <p style={{ margin: '5px 0 0', fontSize: 12.5, color: 'var(--ac-muted)' }}>
              {isAr ? 'من فتح رابط المطالبة، ووين وقف — لكل مصنع، مع بيانات تواصله.' : 'Who opened their claim link and where they stopped — per factory, with contacts.'}
            </p>
          </div>
          <button onClick={load} disabled={loading}
                  style={{ border: '1px solid var(--ac-line)', background: 'var(--ac-card,#fff)', color: 'var(--ac-ink)', borderRadius: 10, padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
            {loading ? (isAr ? 'يحدّث…' : 'Refreshing…') : (isAr ? '↻ تحديث' : '↻ Refresh')}
          </button>
        </div>

        {err && (
          <p style={{ color: '#c0503f', fontSize: 13, marginTop: 14 }}>
            {err === 'admin only' ? (isAr ? 'هذي الصفحة للأدمن فقط.' : 'Admin only.') : err}
          </p>
        )}

        {/* summary cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12, margin: '18px 0' }}>
          {cards.map((c) => {
            const tn = c.tone ? TONE[c.tone] : null;
            return (
              <div key={c.label} style={{ background: 'var(--ac-card,#fff)', border: '1px solid var(--ac-line)', borderRadius: 14, padding: '15px 16px' }}>
                <div style={{ fontSize: 11.5, color: 'var(--ac-muted)', fontWeight: 600 }}>{c.label}</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 5, flexDirection: isAr ? 'row-reverse' : 'row', justifyContent: isAr ? 'flex-end' : 'flex-start' }}>
                  <span style={{ fontSize: 28, fontWeight: 800, color: 'var(--ac-ink)', fontVariantNumeric: 'tabular-nums' }}>{c.val}</span>
                  {c.sub && <span style={{ fontSize: 13, fontWeight: 700, color: tn ? tn.fg : 'var(--ac-muted)' }}>{c.sub}</span>}
                </div>
              </div>
            );
          })}
        </div>

        {/* step-by-step funnel */}
        <section style={{ background: 'var(--ac-card,#fff)', border: '1px solid var(--ac-line)', borderRadius: 16, padding: '18px 18px 8px', marginBottom: 18 }}>
          <h2 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 800, color: 'var(--ac-ink)' }}>{isAr ? 'تحليل الخطوات' : 'Step-by-step'}</h2>
          <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--ac-muted)' }}>
            {isAr ? 'كم وصل لكل مرحلة، وكم تسرّب بينها.' : 'How many reached each stage, and how many dropped between them.'}
          </p>
          {steps.map((s) => {
            const tn = TONE[s.tone];
            return (
              <div key={s.key} style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, marginBottom: 5, flexDirection: isAr ? 'row-reverse' : 'row' }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ac-ink)' }}>{isAr ? s.ar : s.en}</span>
                  <span style={{ fontSize: 12.5, color: 'var(--ac-muted)', fontVariantNumeric: 'tabular-nums' }}>
                    <b style={{ color: 'var(--ac-ink)', fontSize: 15 }}>{s.c}</b> · {s.pct}%
                    {s.drop > 0 && <span style={{ color: '#c0503f', marginInlineStart: 8 }}>▼ {s.drop} {isAr ? 'تسرّبوا' : 'dropped'}</span>}
                  </span>
                </div>
                <div style={{ height: 12, borderRadius: 99, background: 'rgba(0,0,0,0.05)', overflow: 'hidden', direction: isAr ? 'rtl' : 'ltr' }}>
                  <div style={{ width: `${Math.max(s.pct, s.c > 0 ? 3 : 0)}%`, height: '100%', background: tn.fg, borderRadius: 99, transition: 'width .4s ease' }} />
                </div>
              </div>
            );
          })}
        </section>

        <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12.5, color: 'var(--ac-muted)', cursor: 'pointer' }}>
            <input type="checkbox" checked={onlyOpened} onChange={(e) => setOnlyOpened(e.target.checked)} />
            {isAr ? 'اعرض من فتح فقط' : 'Only those who opened'}
          </label>
          {totalErrors > 0 && <span style={{ fontSize: 12, color: '#c0503f', fontWeight: 600 }}>⚠ {isAr ? `${totalErrors} خطأ تقني` : `${totalErrors} errors`}</span>}
          {totalInapp > 0 && <span style={{ fontSize: 12, color: '#946A2B', fontWeight: 600 }}>📱 {isAr ? `${totalInapp} متصفح داخلي` : `${totalInapp} in-app`}</span>}
        </div>

        {/* per-factory cards (mobile-friendly, contact visible) */}
        <div style={{ display: 'grid', gap: 10 }}>
          {shown.map((r) => {
            const st = statusOf(r, isAr);
            const tn = TONE[st.tone];
            const wa = waDigits(r.phone);
            return (
              <div key={r.slug} style={{ border: '1px solid var(--ac-line)', borderRadius: 14, background: 'var(--ac-card,#fff)', padding: '13px 15px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start', flexDirection: isAr ? 'row-reverse' : 'row' }}>
                  <div style={{ minWidth: 0, textAlign: isAr ? 'right' : 'left' }}>
                    <div style={{ fontWeight: 700, color: 'var(--ac-ink)', fontSize: 14 }}>{r.name || r.slug}</div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 3, justifyContent: isAr ? 'flex-end' : 'flex-start' }}>
                      {r.category && <span style={{ fontSize: 10.5, color: 'var(--ac-muted)' }}>{r.category}</span>}
                      <a href={`https://maabar.io/claim/${r.slug}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: 'var(--ac-muted)', textDecoration: 'none' }}>/claim/{r.slug} ↗</a>
                    </div>
                  </div>
                  <span style={{ flexShrink: 0, padding: '3px 10px', borderRadius: 99, fontSize: 11.5, fontWeight: 700, background: tn.bg, color: tn.fg, border: `1px solid ${tn.bd}`, whiteSpace: 'nowrap' }}>{st.t}</span>
                </div>

                {/* contact actions */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 11, direction: 'ltr', justifyContent: isAr ? 'flex-end' : 'flex-start' }}>
                  {r.phone && wa ? (
                    <a href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer"
                       style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#25D366', color: '#fff', textDecoration: 'none', borderRadius: 10, padding: '7px 12px', fontSize: 12.5, fontWeight: 700 }}>
                      ◆ {r.phone}
                    </a>
                  ) : null}
                  {r.email && (
                    <a href={`mailto:${r.email}`}
                       style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(60,96,152,0.10)', color: '#3C6098', textDecoration: 'none', borderRadius: 10, padding: '7px 12px', fontSize: 12, fontWeight: 600, border: '1px solid rgba(60,96,152,0.22)' }}>
                      ✉ {r.email}
                    </a>
                  )}
                  {!r.phone && !r.email && <span style={{ fontSize: 12, color: 'var(--ac-faint,#9C978D)' }}>{isAr ? 'لا بيانات تواصل' : 'no contact'}</span>}
                </div>

                {/* meta */}
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 10, fontSize: 11.5, color: 'var(--ac-muted)', flexDirection: isAr ? 'row-reverse' : 'row', fontVariantNumeric: 'tabular-nums' }}>
                  <span>{(CHAN[r.channel] || CHAN.none)(isAr)}</span>
                  <span>{isAr ? 'زوّار' : 'visitors'}: {r.visitors || 0}</span>
                  <span>{isAr ? 'آخر نشاط' : 'last'}: {fmtKsa(r.last_seen, isAr)}</span>
                  {r.inapp && <span style={{ color: '#946A2B' }}>📱 {r.inapp}</span>}
                  {r.had_error && <span style={{ color: '#c0503f' }}>⚠ {isAr ? 'خطأ' : 'error'}</span>}
                </div>
              </div>
            );
          })}
          {!loading && shown.length === 0 && (
            <div style={{ padding: 28, textAlign: 'center', color: 'var(--ac-muted)', border: '1px solid var(--ac-line)', borderRadius: 14, background: 'var(--ac-card,#fff)' }}>{isAr ? 'لا بيانات بعد.' : 'No data yet.'}</div>
          )}
        </div>

        <p style={{ fontSize: 11.5, color: 'var(--ac-faint,#9C978D)', marginTop: 12, lineHeight: 1.7 }}>
          {isAr
            ? 'يُحدّث لحظيًا. اضغط زر الواتساب لفتح المحادثة مباشرة. «ما فتح» = أُرسل له ولم يُفتح الرابط بعد. للمشاهدة بالفيديو افتح Clarity.'
            : 'Updates live. Tap the WhatsApp button to open the chat. “Not opened” = invited but link not opened yet. For session replay, open Clarity.'}
        </p>
      </div>
    </ConsoleShell>
  );
}
